import { partition } from 'es-toolkit';
import React from 'react';

import { RenderOrder } from '@paragon/three-shared';
import { notNullish } from '@paragon/utilities';

import { useAppSelector } from '../../components/hooks';
import { getSelectedItemIdentifiers } from '../../library/selection/selectors';
import { getCurrentlySelectableItemTypes } from '../../store/selectors/getCurrentlyClickableItemTypes';
import { getVisibleWorkLinesDictionary } from '../../store/selectors/layoutDataSelectors';
import { getActiveWorkLineMode } from '../../store/selectors/layoutVisualOptionsSelectors';
import { createSimpleSetSelector } from '../../store/selectors/util/createSimpleSetSelector';
import { Segments } from './Segments';
import { WorkLineInsertionPickingController } from './WorkLineInsertionController';
import { WorkLineInsertionPreviewVisuals } from './WorkLineInsertionPreviewVisuals';
import { WorkLineSegments, WorkLineVisual } from './WorkLineVisual';

const getSelectedWorkLineGuids = createSimpleSetSelector(getSelectedItemIdentifiers, (items) => {
  return new Set(items.filter((item) => item.type === 'workLine').map((item) => item.guid));
});

export function WorkLinesController() {
  const workLines = useAppSelector(getVisibleWorkLinesDictionary);
  const selectedWorkLineGuids = useAppSelector(getSelectedWorkLineGuids);

  const mode = useAppSelector(getActiveWorkLineMode);

  let [emphasisedLines, normalLines] = partition(Object.values(workLines), (line) =>
    selectedWorkLineGuids.has(line.guid),
  );

  if (mode === 'none') {
    // clear the arrays instead of just returning null here so that the `<Segments>` providers remain mounted and their
    //   GPU geometries are not disposed
    normalLines = emphasisedLines = [];
  }

  return (
    <>
      <Segments dashed depthTest={false} initialSize={100} sizeIncrement={100}>
        {emphasisedLines.map((line) => (
          <WorkLineVisual key={line.guid} line={line} emphasised />
        ))}

        {/* insertion preview visual is always emphasized/dashed */}
        <WorkLineInsertionPreviewVisuals />
      </Segments>

      <Segments depthTest={false} initialSize={10} sizeIncrement={100}>
        {normalLines.map((line) => (
          <WorkLineVisual key={line.guid} line={line} />
        ))}
      </Segments>

      <Segments depthTest={false} initialSize={1} sizeIncrement={1} renderOrder={RenderOrder.WorkLineHoverOverlay}>
        <HoveredWorkLineSegments />
      </Segments>

      <WorkLineInsertionPickingController />
    </>
  );
}

function HoveredWorkLineSegments() {
  const workLines = useAppSelector(getVisibleWorkLinesDictionary);

  const selectedWorkLineGuids = useAppSelector(getSelectedWorkLineGuids);
  const hoveredWorkLineGuids = useAppSelector((state) => state.layout.hoveredWorkLineGuids);

  const mode = useAppSelector(getActiveWorkLineMode);
  const selectable = useAppSelector((state) => getCurrentlySelectableItemTypes(state).has('workLine'));

  if (mode === 'none' || !selectable) return null;

  const hoveredWorkLines = hoveredWorkLineGuids
    .filter((guid) => !selectedWorkLineGuids.has(guid))
    .map((guid) => workLines[guid])
    .filter(notNullish);

  return <WorkLineSegments lines={hoveredWorkLines} emphasised />;
}
